import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { 
  ArrowLeft, 
  Users, 
  Search, 
  Gift, 
  Phone, 
  User, 
  Copy, 
  Check,
  XCircle,
  Loader2,
  UserCircle,
  ClipboardList,
  Pause,
  Play,
  Settings,
  Grid3X3,
  RotateCcw,
  Upload,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';

const SorteoManagement = () => {
  const { sorteoId } = useParams();
  const navigate = useNavigate();
  const [sorteo, setSorteo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedList, setCopiedList] = useState(false);
  const [showClientesList, setShowClientesList] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editData, setEditData] = useState({ title: '', whatsapp_number: '', observations: '' });
  const [expandedParticipant, setExpandedParticipant] = useState(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [editPrizes, setEditPrizes] = useState([]);
  const [editLogo, setEditLogo] = useState(null);
  const [editPrizeImages, setEditPrizeImages] = useState([]);
  const [newGridType, setNewGridType] = useState('');
  const [customNumbers, setCustomNumbers] = useState('');
  const [changingGrid, setChangingGrid] = useState(false);

  useEffect(() => {
    fetchSorteo();
  }, [sorteoId]);

  const fetchSorteo = async () => {
    try {
      const response = await axios.get(`${API}/sorteos/${sorteoId}`);
      setSorteo(response.data);
    } catch (error) {
      console.error('Error fetching sorteo:', error);
      toast.error('Error al cargar el sorteo');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/api/share/sorteo/${sorteoId}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success('Link copiado - pegalo en WhatsApp');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleToggleActive = async () => {
    try {
      const newStatus = !sorteo.is_active;
      await axios.put(`${API}/sorteos/${sorteoId}`, { is_active: newStatus });
      toast.success(newStatus ? 'Sorteo activado' : 'Sorteo pausado');
      fetchSorteo();
    } catch (error) {
      console.error('Error toggling sorteo:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleResetParticipants = async () => {
    const confirmMsg = `¿Reiniciar todos los participantes de este sorteo?\n\n• Todos los números vuelven a "Disponible"\n• Se pierden los nombres/teléfonos asignados\n• El sorteo queda listo para volver a usarse desde cero\n• NO se elimina el sorteo, ni sus clientes registrados\n\nEsto no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const response = await axios.post(`${API}/sorteos/${sorteoId}/reset-participants`);
      toast.success(response.data.message || 'Sorteo reiniciado');
      fetchSorteo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reiniciar el sorteo');
    }
  };

  const handleDuplicate = async () => {
    if (!window.confirm('¿Duplicar este sorteo?\n\nSe creará uno nuevo con el mismo título (+ "(copia)"), premios, fotos y tipo. El sorteo original no se modifica.')) return;
    try {
      const response = await axios.post(`${API}/sorteos/${sorteoId}/duplicate`);
      toast.success('Sorteo duplicado. Abriendo el nuevo...');
      setTimeout(() => {
        navigate(`/admin/manage-sorteo/${response.data.new_sorteo_id}`);
      }, 600);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar el sorteo');
    }
  };

  const handleToggleEditPanel = () => {
    if (!showEditPanel && sorteo) {
      setEditData({
        title: sorteo.title || '',
        whatsapp_number: sorteo.whatsapp_number || '',
        observations: sorteo.observations || ''
      });
      setEditPrizes([...(sorteo.prizes || [])]);
      setEditLogo(sorteo.logo || null);
      setEditPrizeImages([...(sorteo.prize_images || [])]);
      setNewGridType(sorteo.sorteo_type || 'standard');
    }
    setShowEditPanel(!showEditPanel);
  };

  const handleEditImageUpload = (e, type) => {
    const files = Array.from(e.target.files);
    const maxSize = 2 * 1024 * 1024;
    if (type === 'prizes') {
      const total = editPrizeImages.length + files.length;
      if (total > 5) {
        toast.error('Máximo 5 imágenes de premios');
        return;
      }
    }
    for (const file of files) {
      if (file.size > maxSize) {
        toast.error('Las imágenes deben ser menores a 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'logo') {
          setEditLogo(reader.result);
        } else {
          setEditPrizeImages(prev => [...prev, reader.result]);
        }
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleSaveEdit = async () => {
    try {
      const cleanedPrizes = editPrizes.filter(p => p.trim() !== '');
      if (cleanedPrizes.length === 0) {
        toast.error('Agregá al menos un premio');
        return;
      }

      const update = {};
      if (editData.title !== sorteo.title) update.title = editData.title;
      if (editData.whatsapp_number !== sorteo.whatsapp_number) update.whatsapp_number = editData.whatsapp_number;
      if ((editData.observations || '') !== (sorteo.observations || '')) update.observations = editData.observations || null;
      if (JSON.stringify(cleanedPrizes) !== JSON.stringify(sorteo.prizes)) update.prizes = cleanedPrizes;
      if ((editLogo || null) !== (sorteo.logo || null)) update.logo = editLogo;
      if (JSON.stringify(editPrizeImages) !== JSON.stringify(sorteo.prize_images || [])) update.prize_images = editPrizeImages;

      if (Object.keys(update).length > 0) {
        await axios.put(`${API}/sorteos/${sorteoId}`, update);
        toast.success('Datos actualizados');
        fetchSorteo();
      } else {
        toast.info('No hay cambios para guardar');
      }
    } catch (error) {
      toast.error('Error al guardar cambios');
    }
  };

  const handleChangeGrid = async () => {
    if (!newGridType) return;
    const hasPart = sorteo.entries.some(e => e.status !== 'available');
    const msg = hasPart
      ? 'Hay números ocupados. Se intentará preservarlos. ¿Continuar?'
      : '¿Cambiar la grilla? Esto regenerará todos los números.';
    if (!window.confirm(msg)) return;

    setChangingGrid(true);
    try {
      const payload = { sorteo_type: newGridType };
      if (newGridType === 'custom') {
        if (!customNumbers || parseInt(customNumbers) <= 0) {
          toast.error('Ingresá una cantidad válida');
          setChangingGrid(false);
          return;
        }
        payload.total_numbers = parseInt(customNumbers);
      }
      const response = await axios.post(`${API}/sorteos/${sorteoId}/change-grid`, payload);
      toast.success(response.data.message);
      fetchSorteo();
      setShowEditPanel(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar grilla');
    } finally {
      setChangingGrid(false);
    }
  };

  const handleSelectEntry = (entry) => {
    if (selectedEntries.find(e => e.id === entry.id)) {
      setSelectedEntries(prev => prev.filter(e => e.id !== entry.id));
    } else {
      setSelectedEntries(prev => [...prev, entry]);
    }
  };

  const handleLiberarSeleccionados = async () => {
    if (selectedEntries.length === 0) {
      toast.error('Seleccioná números para liberar');
      return;
    }

    if (!window.confirm(`¿Liberar ${selectedEntries.length} número(s)?`)) {
      return;
    }

    try {
      await axios.post(`${API}/sorteos/${sorteoId}/update-status`, {
        entry_ids: selectedEntries.map(e => e.id),
        new_status: 'available'
      });
      toast.success('Números liberados');
      setSelectedEntries([]);
      fetchSorteo();
    } catch (error) {
      console.error('Error liberating entries:', error);
      toast.error('Error al liberar números');
    }
  };

  // Agrupar participantes por teléfono para sorteos de clientes
  const getClientesAgrupados = () => {
    if (!sorteo?.entries) return [];
    
    const clientesMap = {};
    sorteo.entries.forEach(entry => {
      if (entry.participant_phone) {
        if (!clientesMap[entry.participant_phone]) {
          clientesMap[entry.participant_phone] = {
            nombre: entry.participant_name,
            telefono: entry.participant_phone,
            numeros: []
          };
        }
        clientesMap[entry.participant_phone].numeros.push(entry.number);
      }
    });
    
    return Object.values(clientesMap).sort((a, b) => b.numeros.length - a.numeros.length);
  };

  // Agrupar participantes por teléfono para sorteos normales
  const getParticipantesAgrupados = () => {
    if (!sorteo?.entries) return [];
    
    const participantesMap = {};
    sorteo.entries.forEach(entry => {
      if (entry.participant_phone) {
        if (!participantesMap[entry.participant_phone]) {
          participantesMap[entry.participant_phone] = {
            nombre: entry.participant_name,
            telefono: entry.participant_phone,
            numeros: []
          };
        }
        participantesMap[entry.participant_phone].numeros.push(entry.number);
      }
    });
    
    return Object.values(participantesMap).sort((a, b) => b.numeros.length - a.numeros.length);
  };

  // Copiar lista de participantes al portapapeles
  // Ordena ascendente por numero/fila. Para sorteos de filas incluye los numeros de la fila.
  const getEntrySortKey = (entry) => {
    const num = entry.number || '';
    const filaMatch = num.match(/^Fila\s+(\d+)/i);
    if (filaMatch) return parseInt(filaMatch[1], 10);
    const parsed = parseInt(num, 10);
    return isNaN(parsed) ? num : parsed;
  };

  const formatEntryLine = (entry) => {
    const displayNums = entry.display_numbers && entry.display_numbers.length > 0
      ? ` - ${entry.display_numbers.join(', ')}`
      : '';
    return `${entry.number} - ${entry.participant_name}${displayNums}`;
  };

  const handleCopyParticipantsList = () => {
    if (!sorteo?.entries) return;

    const entriesConParticipante = sorteo.entries
      .filter(e => e.participant_phone)
      .sort((a, b) => {
        const ka = getEntrySortKey(a);
        const kb = getEntrySortKey(b);
        if (typeof ka === 'number' && typeof kb === 'number') return ka - kb;
        return String(ka).localeCompare(String(kb));
      });

    if (entriesConParticipante.length === 0) {
      toast.error('No hay participantes para copiar');
      return;
    }

    const texto = entriesConParticipante
      .map(formatEntryLine)
      .join('\n');

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(texto).then(() => {
          setCopiedList(true);
          toast.success('Lista copiada al portapapeles');
          setTimeout(() => setCopiedList(false), 2000);
        }).catch(() => {
          fallbackCopyText(texto);
        });
      } else {
        fallbackCopyText(texto);
      }
    } catch (err) {
      fallbackCopyText(texto);
    }
  };

  // Fallback para copiar texto
  const fallbackCopyText = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopiedList(true);
      toast.success('Lista copiada al portapapeles');
      setTimeout(() => setCopiedList(false), 2000);
    } catch (err) {
      toast.error('No se pudo copiar. Intentá seleccionar el texto manualmente.');
    }
    
    document.body.removeChild(textArea);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 hover:bg-green-200 text-green-800 border-green-300';
      case 'reserved':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredEntries = sorteo?.entries?.filter(entry => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      entry.number.toLowerCase().includes(term) ||
      entry.participant_name?.toLowerCase().includes(term) ||
      entry.participant_phone?.includes(term)
    );
  }) || [];

  // Agrupar participantes por usuario
  const participants = sorteo?.entries?.filter(e => e.participant_phone) || [];
  const stats = {
    total: sorteo?.entries?.length || 0,
    registered: participants.length,
    available: sorteo?.entries?.filter(e => e.status === 'available').length || 0
  };

  const clientesAgrupados = getClientesAgrupados();
  const esSorteoClientes = sorteo?.sorteo_type === 'clientes';

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto mb-4" size={40} />
        <p>Cargando sorteo...</p>
      </div>
    );
  }

  if (!sorteo) {
    return (
      <div className="text-center py-12">
        <p>Sorteo no encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <Button variant="outline" onClick={() => navigate('/admin/sorteos')}>
          <ArrowLeft className="mr-2" size={20} />
          Volver
        </Button>
        <div className="flex gap-2 flex-wrap justify-end">
          {/* Botón de editar */}
          <Button
            onClick={handleToggleEditPanel}
            variant={showEditPanel ? 'default' : 'outline'}
            data-testid="edit-sorteo-btn"
          >
            <Settings className="mr-2" size={18} />
            {showEditPanel ? 'Cerrar' : 'Editar'}
          </Button>
          {/* Botón de pausa/activar */}
          <Button
            onClick={handleToggleActive}
            className={sorteo.is_active ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-600 hover:bg-green-700'}
            data-testid="toggle-active-btn"
          >
            {sorteo.is_active ? (
              <>
                <Pause className="mr-2" size={18} />
                Pausar
              </>
            ) : (
              <>
                <Play className="mr-2" size={18} />
                Activar
              </>
            )}
          </Button>
          {/* Botón de compartir lista - disponible para TODOS los sorteos con participantes */}
          {participants.length > 0 && (
            <Button
              onClick={handleCopyParticipantsList}
              className="bg-green-600 hover:bg-green-700"
              data-testid="copy-participants-list-btn"
            >
              {copiedList ? (
                <>
                  <Check className="mr-2" size={18} />
                  ¡Copiado!
                </>
              ) : (
                <>
                  <Copy className="mr-2" size={18} />
                  Compartir Lista
                </>
              )}
            </Button>
          )}
          {esSorteoClientes && (
            <Button
              onClick={() => setShowClientesList(!showClientesList)}
              variant="outline"
              className="border-purple-300 text-purple-700"
            >
              <UserCircle className="mr-2" size={18} />
              {showClientesList ? 'Ocultar Detalle' : 'Ver Detalle'}
            </Button>
          )}
          {!esSorteoClientes && (
            <Button
              onClick={handleCopyLink}
              variant="outline"
              className="border-green-300 text-green-700"
            >
              {copiedLink ? (
                <>
                  <Check className="mr-2" size={18} />
                  ¡Copiado!
                </>
              ) : (
                <>
                  <Copy className="mr-2" size={18} />
                  Copiar Link
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Panel de Edición */}
      {showEditPanel && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Settings size={20} />
              Editar Sorteo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Título</Label>
                <Input
                  value={editData.title}
                  onChange={(e) => setEditData({...editData, title: e.target.value})}
                  data-testid="edit-sorteo-title"
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={editData.whatsapp_number}
                  onChange={(e) => setEditData({...editData, whatsapp_number: e.target.value})}
                  data-testid="edit-sorteo-whatsapp"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Observaciones</Label>
                <textarea
                  value={editData.observations}
                  onChange={(e) => setEditData({...editData, observations: e.target.value})}
                  placeholder="Aclaraciones para los participantes"
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="edit-sorteo-observations"
                />
              </div>
            </div>
            {/* Premios */}
            <div>
              <Label>Premios</Label>
              <div className="space-y-2">
                {editPrizes.map((prize, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={prize}
                      onChange={(e) => {
                        const newPrizes = [...editPrizes];
                        newPrizes[idx] = e.target.value;
                        setEditPrizes(newPrizes);
                      }}
                      placeholder={`Premio ${idx + 1}`}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditPrizes(editPrizes.filter((_, i) => i !== idx))}
                      className="text-red-600 shrink-0"
                    >
                      <XCircle size={16} />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditPrizes([...editPrizes, ''])}
                >
                  + Agregar premio
                </Button>
              </div>
            </div>

            {/* Logo */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Logo</Label>
              <div className="mt-2">
                {editLogo ? (
                  <div className="relative inline-block">
                    <img src={editLogo} alt="Logo" className="w-32 h-32 object-contain border rounded bg-white" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setEditLogo(null)}
                      className="absolute -top-2 -right-2"
                      data-testid="edit-sorteo-remove-logo"
                    >
                      <XCircle size={14} />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer inline-block">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-500 transition-colors">
                      <Upload className="mx-auto mb-1" size={24} />
                      <p className="text-xs text-gray-600">Subir logo</p>
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => handleEditImageUpload(e, 'logo')} className="hidden" data-testid="edit-sorteo-logo-input" />
                  </label>
                )}
              </div>
            </div>

            {/* Imágenes de premios */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Imágenes de premios (máx. 5)</Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {editPrizeImages.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img src={img} alt={`Premio ${idx + 1}`} className="w-24 h-24 object-cover border rounded" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setEditPrizeImages(editPrizeImages.filter((_, i) => i !== idx))}
                      className="absolute -top-2 -right-2"
                    >
                      <XCircle size={14} />
                    </Button>
                  </div>
                ))}
                {editPrizeImages.length < 5 && (
                  <label className="cursor-pointer">
                    <div className="w-24 h-24 border-2 border-dashed rounded flex items-center justify-center hover:bg-gray-50 transition-colors">
                      <Plus size={24} className="text-gray-400" />
                    </div>
                    <input type="file" accept="image/*" multiple onChange={(e) => handleEditImageUpload(e, 'prizes')} className="hidden" data-testid="edit-sorteo-prize-images-input" />
                  </label>
                )}
              </div>
            </div>

            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="save-sorteo-edit-btn">
              Guardar Cambios
            </Button>

            {/* Cambiar Grilla */}
            <div className="border-t pt-4">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                <Grid3X3 size={18} />
                Cambiar Tipo de Grilla
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                Grilla actual: <strong>{sorteo.sorteo_type === 'grid_100' ? '00-99 (100)' : sorteo.sorteo_type === 'standard' ? '000-999 (1000)' : sorteo.sorteo_type === 'grid_10000' ? '0000-9999 (10000)' : sorteo.sorteo_type === 'lines' ? '200 Líneas x 5' : sorteo.sorteo_type === 'lines_125' ? '125 Líneas x 8' : sorteo.sorteo_type === 'lines_250' ? '250 filas Manu' : sorteo.sorteo_type === 'lines_250_osvaldo' ? '250 Líneas Osvaldo' : sorteo.sorteo_type === 'lines_50' ? '50 Líneas x 2' : sorteo.sorteo_type === 'lines_500' ? '500 Líneas x 2' : sorteo.sorteo_type === 'lines_20' ? '20 Líneas x 5' : `Personalizada (${sorteo.total_numbers})`}</strong>
              </p>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="min-w-[200px]">
                  <Label>Nuevo tipo</Label>
                  <Select value={newGridType} onValueChange={setNewGridType}>
                    <SelectTrigger data-testid="sorteo-grid-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grid_100">00-99 (100 números)</SelectItem>
                      <SelectItem value="standard">000-999 (1000 números)</SelectItem>
                      <SelectItem value="grid_10000">0000-9999 (10000 números)</SelectItem>
                      <SelectItem value="lines_20">20 Líneas x 5 (00-99)</SelectItem>
                      <SelectItem value="lines_50">50 Líneas x 2</SelectItem>
                      <SelectItem value="lines_500">500 Líneas x 2</SelectItem>
                      <SelectItem value="lines">200 Líneas x 5</SelectItem>
                      <SelectItem value="lines_125">125 Líneas x 8</SelectItem>
                      <SelectItem value="lines_250">250 filas Manu</SelectItem>
                      <SelectItem value="lines_250_osvaldo">250 Líneas Osvaldo</SelectItem>
                      <SelectItem value="custom">Personalizada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newGridType === 'custom' && (
                  <div className="min-w-[150px]">
                    <Label>Cantidad</Label>
                    <Input
                      type="number"
                      min="1"
                      value={customNumbers}
                      onChange={(e) => setCustomNumbers(e.target.value)}
                      placeholder="Ej: 500"
                    />
                  </div>
                )}
                <Button
                  onClick={handleChangeGrid}
                  disabled={changingGrid || newGridType === sorteo.sorteo_type}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="change-sorteo-grid-btn"
                >
                  {changingGrid ? 'Cambiando...' : 'Cambiar Grilla'}
                </Button>
              </div>
              {sorteo.entries?.some(e => e.status !== 'available') && (
                <p className="text-sm text-amber-700 mt-2 bg-amber-50 p-2 rounded">
                  Hay números ocupados. Al cambiar la grilla se intentará preservarlos.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Gift className="text-green-600" />
                {sorteo.title}
              </CardTitle>
              <CardDescription className="flex items-center gap-2">
                {esSorteoClientes ? (
                  <Badge className="bg-purple-600">
                    <UserCircle size={14} className="mr-1" />
                    Sorteo para Clientes
                  </Badge>
                ) : (
                  sorteo.sorteo_type === 'standard' ? '1000 Números (000-999)' : `${sorteo.total_numbers} Números`
                )}
              </CardDescription>
            </div>
            <Badge variant={sorteo.is_active ? "default" : "secondary"} className="text-lg px-4 py-1">
              {sorteo.is_active ? 'Activo' : 'Inactivo'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
              <p className="text-sm text-gray-600">Total Números</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{esSorteoClientes ? clientesAgrupados.length : stats.registered}</p>
              <p className="text-sm text-gray-600">{esSorteoClientes ? 'Clientes' : 'Participantes'}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-600">{stats.available}</p>
              <p className="text-sm text-gray-600">Disponibles</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Clientes del Sorteo (Solo para sorteos de clientes) */}
      {esSorteoClientes && showClientesList && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2 text-purple-800">
                <ClipboardList size={20} />
                Lista de Participantes
              </CardTitle>
              <Button
                onClick={handleCopyParticipantsList}
                className="bg-green-600 hover:bg-green-700"
              >
                {copiedList ? (
                  <>
                    <Check className="mr-2" size={18} />
                    ¡Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2" size={18} />
                    Compartir Lista
                  </>
                )}
              </Button>
            </div>
            <CardDescription>
              Lista para compartir en el grupo (sin teléfonos)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {clientesAgrupados.map((cliente, index) => (
                <div 
                  key={cliente.telefono}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border border-purple-100 shadow-sm"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      index === 0 ? 'bg-yellow-500' :
                      index === 1 ? 'bg-gray-400' :
                      index === 2 ? 'bg-amber-600' :
                      'bg-purple-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 flex items-center gap-2">
                        <User size={16} className="text-purple-600" />
                        {cliente.nombre}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex flex-wrap gap-1 justify-end max-w-md">
                      {/* Mostrar cada número individualmente */}
                      {cliente.numeros.map(num => (
                        <span key={num} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-sm font-mono">
                          {num}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {cliente.numeros.length} número{cliente.numeros.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Preview del texto que se copiará */}
            <div className="mt-4 p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600 mb-2 font-semibold">Vista previa del texto a compartir:</p>
              <div className="text-xs text-gray-700 whitespace-pre-wrap font-mono bg-white p-3 rounded border max-h-40 overflow-y-auto">
                {`${sorteo.title}\n\n${
                  sorteo.entries
                    .filter(e => e.participant_phone)
                    .sort((a, b) => {
                      const ka = getEntrySortKey(a);
                      const kb = getEntrySortKey(b);
                      if (typeof ka === 'number' && typeof kb === 'number') return ka - kb;
                      return String(ka).localeCompare(String(kb));
                    })
                    .slice(0, 5)
                    .map(formatEntryLine)
                    .join('\n')
                }${sorteo.entries.filter(e => e.participant_phone).length > 5 ? '\n...' : ''}\n\nTotal: ${sorteo.entries.filter(e => e.participant_phone).length}`}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participantes (vista normal para sorteos regulares) */}
      {!esSorteoClientes && participants.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <Users size={20} />
                Participantes ({getParticipantesAgrupados().length} personas, {participants.length} números)
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-3 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input
                type="text"
                placeholder="Buscar por nombre o teléfono..."
                value={participantSearch}
                onChange={(e) => setParticipantSearch(e.target.value)}
                className="pl-9"
                data-testid="participant-search"
              />
              {participantSearch && (
                <button
                  type="button"
                  onClick={() => setParticipantSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Limpiar"
                >
                  <XCircle size={16} />
                </button>
              )}
            </div>
            {(() => {
              const all = getParticipantesAgrupados();
              const q = participantSearch.trim().toLowerCase();
              const filtered = q
                ? all.filter(p =>
                    (p.nombre || '').toLowerCase().includes(q) ||
                    (p.telefono || '').toLowerCase().includes(q)
                  )
                : all;
              if (filtered.length === 0) {
                return (
                  <p className="text-sm text-gray-500 text-center py-6" data-testid="participant-search-empty">
                    No hay participantes que coincidan con &quot;{participantSearch}&quot;
                  </p>
                );
              }
              return (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filtered.map((participante, index) => {
                const isExpanded = expandedParticipant === participante.telefono;
                // entries of this participant
                const participantEntries = sorteo.entries.filter(e => e.participant_phone === participante.telefono);
                const selectedFromHere = selectedEntries.filter(e => participantEntries.find(pe => pe.id === e.id));
                const allSelected = selectedFromHere.length === participantEntries.length && participantEntries.length > 0;
                return (
                  <div key={participante.telefono} className="bg-gray-50 rounded-lg border border-transparent hover:border-gray-300 transition-colors">
                    <button
                      type="button"
                      onClick={() => setExpandedParticipant(isExpanded ? null : participante.telefono)}
                      className="w-full flex items-center justify-between p-3 text-left"
                      data-testid={`participant-${participante.telefono}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm shrink-0 ${
                          index === 0 ? 'bg-yellow-500' :
                          index === 1 ? 'bg-gray-400' :
                          index === 2 ? 'bg-amber-600' :
                          'bg-blue-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold flex items-center gap-1 truncate">
                            <User size={14} />
                            {participante.nombre}
                          </p>
                          <p className="text-sm text-gray-500 flex items-center gap-1 truncate">
                            <Phone size={12} />
                            {participante.telefono}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <div className="flex flex-wrap gap-1 justify-end max-w-[180px] sm:max-w-xs">
                          {!isExpanded && participante.numeros.slice(0, 6).map(num => (
                            <span key={num} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-mono">
                              {num}
                            </span>
                          ))}
                          {!isExpanded && participante.numeros.length > 6 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              +{participante.numeros.length - 6}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {participante.numeros.length} número{participante.numeros.length > 1 ? 's' : ''} {isExpanded ? '▾' : '▸'}
                        </p>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-gray-200 pt-3 space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {participantEntries
                            .sort((a, b) => parseInt(a.number) - parseInt(b.number))
                            .map(e => {
                              const isSel = !!selectedEntries.find(se => se.id === e.id);
                              return (
                                <button
                                  type="button"
                                  key={e.id}
                                  onClick={(ev) => { ev.stopPropagation(); handleSelectEntry(e); }}
                                  className={`px-3 py-1.5 rounded font-mono text-sm border transition-all ${
                                    isSel
                                      ? 'bg-purple-600 text-white border-purple-700 ring-2 ring-purple-300'
                                      : e.status === 'paid'
                                        ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                                        : 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                                  }`}
                                  data-testid={`entry-chip-${e.id}`}
                                >
                                  {e.number}
                                </button>
                              );
                            })}
                        </div>

                        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              if (allSelected) {
                                setSelectedEntries(prev => prev.filter(e => !participantEntries.find(pe => pe.id === e.id)));
                              } else {
                                setSelectedEntries(prev => {
                                  const others = prev.filter(e => !participantEntries.find(pe => pe.id === e.id));
                                  return [...others, ...participantEntries];
                                });
                              }
                            }}
                            className="text-xs"
                          >
                            {allSelected ? 'Deselec.' : 'Seleccionar todos'}
                          </Button>
                          {selectedFromHere.length > 0 && (
                            <Button
                              type="button"
                              size="sm"
                              onClick={(ev) => { ev.stopPropagation(); handleLiberarSeleccionados(); }}
                              className="bg-red-600 hover:bg-red-700 text-xs"
                              data-testid={`liberar-from-participant-${participante.telefono}`}
                            >
                              <XCircle size={14} className="mr-1" />
                              Liberar ({selectedFromHere.length})
                            </Button>
                          )}
                          <p className="text-xs text-gray-500 self-center ml-auto">
                            Tocá un número para seleccionarlo y después &quot;Liberar&quot;.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Grilla de números */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Grilla de Números</CardTitle>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar número o participante..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              {selectedEntries.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleLiberarSeleccionados}
                  className="text-red-600"
                >
                  <XCircle size={16} className="mr-1" />
                  Liberar ({selectedEntries.length})
                </Button>
              )}
            </div>
          </div>
          <div className="flex gap-4 text-sm mt-2">
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 bg-green-200 rounded"></div> Disponible
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 bg-blue-200 rounded"></div> Ocupado
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-10 sm:grid-cols-15 md:grid-cols-20 gap-1 max-h-96 overflow-y-auto p-2">
            {filteredEntries.map((entry) => (
              <button
                key={entry.id}
                onClick={() => entry.status !== 'available' && handleSelectEntry(entry)}
                className={`
                  p-1 text-xs font-mono rounded border transition-all
                  ${getStatusColor(entry.status)}
                  ${selectedEntries.find(e => e.id === entry.id) ? 'ring-2 ring-purple-500' : ''}
                  ${entry.status === 'available' ? 'cursor-default' : 'cursor-pointer'}
                `}
                title={entry.participant_name ? `${entry.participant_name} - ${entry.participant_phone}` : 'Disponible'}
              >
                {entry.number}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Link para compartir - Solo para sorteos normales, no de clientes */}
      {!esSorteoClientes && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-1">Link para compartir en WhatsApp:</h3>
            <p className="text-xs text-gray-600 mb-3">Este link muestra la imagen y datos del sorteo al pegarlo en WhatsApp.</p>
            <div className="flex gap-2">
              <code className="flex-1 bg-white p-3 rounded border text-sm break-all" data-testid="sorteo-share-link">
                {`${window.location.origin}/api/share/sorteo/${sorteoId}`}
              </code>
              <Button onClick={handleCopyLink} className="bg-green-600 hover:bg-green-700" data-testid="copy-sorteo-share-link">
                {copiedLink ? <Check size={18} /> : <Copy size={18} />}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zona de acciones avanzadas */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <h3 className="font-semibold text-amber-900 flex items-center gap-2 mb-1">
                <Copy size={18} />
                Duplicar sorteo
              </h3>
              <p className="text-xs text-amber-800">
                Crea una copia independiente con el mismo título, premios, fotos y tipo. Ideal para lanzar otra edición sin perder la actual.
              </p>
            </div>
            <Button
              onClick={handleDuplicate}
              variant="outline"
              className="bg-white border-amber-400 text-amber-900 hover:bg-amber-100"
              data-testid="duplicate-sorteo"
            >
              <Copy size={16} className="mr-2" />
              Duplicar sorteo
            </Button>
          </div>
          <div className="border-t border-amber-200" />
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <h3 className="font-semibold text-amber-900 flex items-center gap-2 mb-1">
                <RotateCcw size={18} />
                Reiniciar sorteo
              </h3>
              <p className="text-xs text-amber-800">
                Pasa todos los números a &quot;Disponible&quot; para reusar este mismo sorteo (mismo título, premios, fotos). Tus clientes registrados no se borran.
              </p>
            </div>
            <Button
              onClick={handleResetParticipants}
              variant="outline"
              className="bg-white border-amber-400 text-amber-900 hover:bg-amber-100"
              data-testid="reset-sorteo-participants"
            >
              <RotateCcw size={16} className="mr-2" />
              Reiniciar participantes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SorteoManagement;
